import { Alert, AlertDescription, Button, Input, Label, Skeleton } from '@sitewise/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';


import { acceptInvitation, inspectInvitation } from '../../api/auth.js';
import { ApiError } from '../../lib/api-client.js';

import { AuthShell } from './auth-shell.js';

export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const inspect = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => inspectInvitation(token),
    enabled: token.length > 0,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () => acceptInvitation({ token, password }),
    onSuccess: () => navigate('/dashboard', { replace: true }),
  });

  if (!token) {
    return (
      <AuthShell title="Invalid invitation">
        <Alert variant="danger">
          <AlertDescription>
            This invitation link is missing required parameters. Ask your administrator to
            re-send the invitation.
          </AlertDescription>
        </Alert>
      </AuthShell>
    );
  }

  if (inspect.isPending) {
    return (
      <AuthShell title="Verifying invitation">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </AuthShell>
    );
  }

  if (inspect.isError || !inspect.data) {
    return (
      <AuthShell title="Invitation no longer valid">
        <Alert variant="danger">
          <AlertDescription>
            This invitation has expired or already been used. Ask your administrator to send a
            new one.
          </AlertDescription>
        </Alert>
      </AuthShell>
    );
  }

  const preview = inspect.data;

  return (
    <AuthShell
      title={`Welcome to ${preview.orgName}`}
      description={`Set a password for ${preview.email} to finish creating your account.`}
      footer={
        <Link to="/login" className="text-fg hover:underline">
          Already activated? Sign in
        </Link>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (password !== confirm) {
            setValidationError('Passwords do not match');
            return;
          }
          setValidationError(null);
          mutation.mutate();
        }}
        noValidate
      >
        <div className="space-y-1.5">
          <Label htmlFor="password">Choose a password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        {validationError ? (
          <Alert variant="danger">
            <AlertDescription>{validationError}</AlertDescription>
          </Alert>
        ) : null}
        {mutation.error ? (
          <Alert variant="danger">
            <AlertDescription>
              {mutation.error instanceof ApiError
                ? mutation.error.message
                : 'Could not activate your account.'}
            </AlertDescription>
          </Alert>
        ) : null}
        <Button type="submit" className="w-full" disabled={mutation.isPending}>
          {mutation.isPending ? 'Activating…' : 'Activate account'}
        </Button>
      </form>
    </AuthShell>
  );
}
